import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { 
    Mail, CheckCircle, XCircle, AlertTriangle, Clock, 
    RefreshCw, TrendingUp, Building2, Zap, Hand, Database, UserPlus
} from 'lucide-react';

const API = `/api`;

const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2
    }).format(amount);
};

const StatCard = ({ icon: Icon, label, value, subValue, color, bgColor }) => (
    <div className={`${bgColor} rounded-lg p-3 border border-slate-200`}>
        <div className="flex items-center gap-2 mb-1">
            <Icon className={`w-4 h-4 ${color}`} />
            <span className="text-xs text-slate-500 font-medium">{label}</span>
        </div>
        <div className={`text-xl font-bold ${color}`}>{value}</div>
        {subValue && <div className="text-xs text-slate-400 mt-0.5">{subValue}</div>}
    </div>
);

const BankBadge = ({ bank, count, amount, auto, manual }) => {
    const getBankColor = (bankName) => {
        const colors = {
            'TD Bank': 'bg-green-100 text-green-700 border-green-200',
            'Capital One': 'bg-red-100 text-red-700 border-red-200',
            'Wise': 'bg-emerald-100 text-emerald-700 border-emerald-200',
            'PayPal': 'bg-blue-100 text-blue-700 border-blue-200',
            'Cash App': 'bg-emerald-100 text-emerald-700 border-emerald-200',
            'Zelle Generic': 'bg-purple-100 text-purple-700 border-purple-200',
            'Chase': 'bg-sky-100 text-sky-700 border-sky-200',
            'PNC Bank': 'bg-orange-100 text-orange-700 border-orange-200',
            'Regions Bank': 'bg-teal-100 text-teal-700 border-teal-200',
            'Bank of America': 'bg-rose-100 text-rose-700 border-rose-200',
        };
        return colors[bankName] || 'bg-slate-100 text-slate-700 border-slate-200';
    };

    return (
        <div className={`${getBankColor(bank)} rounded-lg px-3 py-2 border text-sm`}>
            <div className="font-semibold">{bank}</div>
            <div className="flex items-center gap-2 text-xs mt-1">
                <span>{count} verificaciones</span>
                <span>•</span>
                <span>{formatCurrency(amount)}</span>
            </div>
            <div className="flex items-center gap-2 text-xs mt-0.5 opacity-75">
                <Zap className="w-3 h-3" />
                <span>{auto}</span>
                <Hand className="w-3 h-3 ml-1" />
                <span>{manual}</span>
            </div>
        </div>
    );
};

export const VerificationStatsPanel = () => {
    const { getAuthHeaders } = useAuth();
    const [stats, setStats] = useState(null);
    const [cacheStatus, setCacheStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [lastUpdate, setLastUpdate] = useState(null);

    const fetchStats = async (showRefresh = false) => {
        if (showRefresh) setRefreshing(true);
        try {
            const [statsRes, cacheRes] = await Promise.all([
                axios.get(`${API}/verification-stats`, { headers: getAuthHeaders() }),
                axios.get(`${API}/cache-status`, { headers: getAuthHeaders() }).catch(() => null)
            ]);
            setStats(statsRes.data);
            if (cacheRes) setCacheStatus(cacheRes.data);
            setLastUpdate(new Date());
        } catch (error) {
            console.error('Error fetching verification stats:', error);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };
    
    const [restartingReader, setRestartingReader] = useState(false);
    const restartReader = async () => {
        setRestartingReader(true);
        try {
            const res = await axios.post(`${API}/imap-restart`, {}, { headers: getAuthHeaders() });
            toast.success(res.data.message || 'Lector reiniciado');
            setTimeout(() => { fetchStats(); setRestartingReader(false); }, 5000);
        } catch (error) {
            console.error('Error restarting reader:', error);
            toast.error('No se pudo reiniciar el lector');
            setRestartingReader(false);
        }
    };

    const refreshCache = async () => {
        try {
            await axios.post(`${API}/cache-refresh`, {}, { headers: getAuthHeaders() });
            // Re-fetch stats after triggering refresh
            setTimeout(() => fetchStats(), 2000);
        } catch (error) {
            console.error('Error refreshing cache:', error);
        }
    };

    useEffect(() => {
        fetchStats();
        // Auto-refresh every 30 seconds
        const interval = setInterval(() => fetchStats(), 30000);
        return () => clearInterval(interval);
    }, []);

    if (loading) {
        return (
            <Card className="bg-gradient-to-br from-slate-50 to-sky-50 border-sky-200">
                <CardContent className="p-6">
                    <div className="flex items-center justify-center gap-2 text-slate-500">
                        <RefreshCw className="w-5 h-5 animate-spin" />
                        <span>Cargando estadísticas...</span>
                    </div>
                </CardContent>
            </Card>
        );
    }

    if (!stats) return null;

    return (
        <Card className="bg-gradient-to-br from-slate-50 to-sky-50 border-sky-200" data-testid="verification-stats-panel">
            <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                        <Mail className="w-5 h-5 text-sky-600" />
                        Panel de Estadísticas en Tiempo Real
                    </CardTitle>
                    <div className="flex items-center gap-2">
                        {lastUpdate && (
                            <span className="text-xs text-slate-400">
                                Actualizado: {lastUpdate.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
                            </span>
                        )}
                        <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => fetchStats(true)}
                            disabled={refreshing}
                            className="h-7"
                        >
                            <RefreshCw className={`w-3 h-3 mr-1 ${refreshing ? 'animate-spin' : ''}`} />
                            {refreshing ? 'Actualizando...' : 'Actualizar'}
                        </Button>
                    </div>
                </div>
            </CardHeader>
            <CardContent className="pt-2">
                {/* Main Stats Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-4">
                    <StatCard 
                        icon={CheckCircle}
                        label="Auto-Verificadas"
                        value={stats.auto_verified_invoices}
                        subValue={`${stats.verification_rate}% del total`}
                        color="text-emerald-600"
                        bgColor="bg-emerald-50"
                    />
                    <StatCard 
                        icon={Hand}
                        label="Verificación Manual"
                        value={stats.manual_verified_invoices}
                        color="text-blue-600"
                        bgColor="bg-blue-50"
                    />
                    <StatCard 
                        icon={XCircle}
                        label="Sin Verificar"
                        value={stats.unverified_invoices}
                        color="text-slate-600"
                        bgColor="bg-slate-50"
                    />
                    <StatCard 
                        icon={Mail}
                        label="Correos Usados"
                        value={stats.emails_used}
                        subValue="IDs únicos"
                        color="text-sky-600"
                        bgColor="bg-sky-50"
                    />
                    <StatCard 
                        icon={AlertTriangle}
                        label="Duplicados Detectados"
                        value={stats.duplicates_detected}
                        subValue={`${stats.duplicates_pending_review} pendientes`}
                        color="text-amber-600"
                        bgColor="bg-amber-50"
                    />
                    <StatCard 
                        icon={TrendingUp}
                        label="Total Facturas"
                        value={stats.total_invoices}
                        color="text-violet-600"
                        bgColor="bg-violet-50"
                    />
                </div>

                {/* Today's Stats */}
                <div className="bg-white rounded-lg border border-sky-200 p-4 mb-4">
                    <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                        <Clock className="w-4 h-4 text-sky-500" />
                        Estadísticas de Hoy
                        {stats.extras && <span className="text-xs font-normal text-slate-400">(usuarios regulares)</span>}
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="text-center">
                            <div className="text-2xl font-bold text-sky-600">{stats.today_verified}</div>
                            <div className="text-xs text-slate-500">Verificaciones</div>
                        </div>
                        <div className="text-center">
                            <div className="text-2xl font-bold text-emerald-600">{stats.today_auto}</div>
                            <div className="text-xs text-slate-500">Automáticas</div>
                        </div>
                        <div className="text-center">
                            <div className="text-2xl font-bold text-blue-600">{stats.today_manual}</div>
                            <div className="text-xs text-slate-500">Manuales</div>
                        </div>
                        <div className="text-center">
                            <div className="text-2xl font-bold text-violet-600">{formatCurrency(stats.today_amount)}</div>
                            <div className="text-xs text-slate-500">Monto Total</div>
                        </div>
                    </div>
                </div>

                {/* Extra users - separate accounting (admin only) */}
                {stats.extras && (
                    <div className="bg-fuchsia-50/70 rounded-lg border border-fuchsia-200 p-4 mb-4" data-testid="extras-stats-block">
                        <h4 className="text-sm font-semibold text-fuchsia-800 mb-1 flex items-center gap-2">
                            <UserPlus className="w-4 h-4 text-fuchsia-600" />
                            Usuarios Extra (Aparte) · Hoy
                        </h4>
                        <p className="text-xs text-fuchsia-700/70 mb-3">Contabilidad separada · no suman en las estadísticas regulares</p>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div className="text-center">
                                <div className="text-2xl font-bold text-fuchsia-700" data-testid="extras-today-verified">{stats.extras.today_verified}</div>
                                <div className="text-xs text-slate-500">Verificaciones</div>
                            </div>
                            <div className="text-center">
                                <div className="text-2xl font-bold text-emerald-600" data-testid="extras-today-auto">{stats.extras.today_auto}</div>
                                <div className="text-xs text-slate-500">Automáticas</div>
                            </div>
                            <div className="text-center">
                                <div className="text-2xl font-bold text-blue-600" data-testid="extras-today-manual">{stats.extras.today_manual}</div>
                                <div className="text-xs text-slate-500">Manuales</div>
                            </div>
                            <div className="text-center">
                                <div className="text-2xl font-bold text-fuchsia-700" data-testid="extras-today-amount">{formatCurrency(stats.extras.today_amount)}</div>
                                <div className="text-xs text-slate-500">Monto Total</div>
                            </div>
                        </div>
                        <div className="mt-3 pt-3 border-t border-fuchsia-200 flex flex-wrap gap-x-6 gap-y-1 text-xs text-fuchsia-800" data-testid="extras-alltime">
                            <span>Total facturas extra: <b>{stats.extras.total_invoices}</b></span>
                            <span>Confirmadas: <b>{stats.extras.confirmed_invoices}</b></span>
                            <span>Pendientes: <b>{stats.extras.pending_invoices}</b></span>
                        </div>
                    </div>
                )}

                {/* Banks Today */}
                {stats.by_bank_today && stats.by_bank_today.length > 0 && (
                    <div>
                        <h4 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
                            <Building2 className="w-4 h-4 text-sky-500" />
                            Verificaciones por Banco (Hoy)
                        </h4>
                        <div className="flex flex-wrap gap-2">
                            {stats.by_bank_today.map((bank) => (
                                <BankBadge 
                                    key={bank.bank}
                                    bank={bank.bank}
                                    count={bank.count}
                                    amount={bank.amount}
                                    auto={bank.auto}
                                    manual={bank.manual}
                                />
                            ))}
                        </div>
                    </div>
                )}

                {/* Email Cache Status */}
                {cacheStatus && (
                    <div className="mt-4 bg-slate-50 rounded-lg border border-slate-200 p-3">
                        <div className="flex items-center justify-between mb-2">
                            <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                                <Mail className="w-4 h-4 text-slate-500" />
                                Sistema de Verificación Rápida
                                {cacheStatus.idle_connected && (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-emerald-100 text-emerald-700">
                                        <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                                        Tiempo Real
                                    </span>
                                )}
                                {cacheStatus.using_redis && (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-red-100 text-red-700">
                                        <Database className="w-3 h-3" />
                                        Redis
                                    </span>
                                )}
                            </h4>
                            <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={refreshCache}
                                disabled={cacheStatus.is_updating}
                                className="h-6 text-xs"
                            >
                                <RefreshCw className={`w-3 h-3 mr-1 ${cacheStatus.is_updating ? 'animate-spin' : ''}`} />
                                {cacheStatus.is_updating ? 'Actualizando...' : 'Refrescar'}
                            </Button>
                        </div>
                        <div className="grid grid-cols-4 gap-3 text-center">
                            <div>
                                <div className="text-lg font-bold text-slate-700">{cacheStatus.email_count}</div>
                                <div className="text-xs text-slate-500">Total caché</div>
                            </div>
                            <div>
                                <div className="text-lg font-bold text-sky-600">{cacheStatus.recent_count || 0}</div>
                                <div className="text-xs text-slate-500">Recientes (15m)</div>
                            </div>
                            <div>
                                <div className={`text-lg font-bold ${cacheStatus.idle_connected ? 'text-emerald-600' : 'text-amber-600'}`}>
                                    {cacheStatus.idle_connected ? 'Conectado' : 'Desconectado'}
                                </div>
                                <div className="text-xs text-slate-500">IMAP IDLE</div>
                            </div>
                            <div>
                                <div className={`text-lg font-bold ${cacheStatus.using_redis ? 'text-red-600' : 'text-slate-600'}`}>
                                    {cacheStatus.using_redis ? 'Redis' : 'Memoria'}
                                </div>
                                <div className="text-xs text-slate-500">Almacenamiento</div>
                            </div>
                        </div>
                        {cacheStatus.idle_connected && (
                            <div className="mt-2 text-xs text-emerald-600 text-center">
                                📧 Detectando correos nuevos en tiempo real
                            </div>
                        )}
                        {cacheStatus.imap_health && (() => {
                            const h = cacheStatus.imap_health;
                            const fmt = (iso) => iso ? new Date(iso).toLocaleString('es-ES', { timeZone: 'America/New_York', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—';
                            const stuck = h.lock_held_seconds > 900;
                            const stale = h.last_success_at && (Date.now() - new Date(h.last_success_at).getTime()) > 30 * 60 * 1000;
                            const problem = !h.credentials_configured || stuck || stale || (!h.last_success_at && h.last_error);
                            return (
                                <div
                                    className={`mt-3 rounded-lg border p-2.5 text-xs ${problem ? 'bg-red-50 border-red-200 text-red-800' : 'bg-white border-slate-200 text-slate-600'}`}
                                    data-testid="imap-health"
                                >
                                    <div className="font-semibold mb-1 flex items-center justify-between gap-2">
                                        <span className="flex items-center gap-1.5">
                                            {problem ? <AlertTriangle className="w-3.5 h-3.5" /> : <CheckCircle className="w-3.5 h-3.5 text-emerald-600" />}
                                            Salud lectura de correos (IMAP)
                                        </span>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={restartReader}
                                            disabled={restartingReader}
                                            className={`h-6 text-xs ${problem ? 'border-red-300 text-red-700 hover:bg-red-100' : 'text-slate-600'}`}
                                            data-testid="imap-restart-btn"
                                        >
                                            <RefreshCw className={`w-3 h-3 mr-1 ${restartingReader ? 'animate-spin' : ''}`} />
                                            {restartingReader ? 'Reiniciando...' : 'Reiniciar lector'}
                                        </Button>
                                    </div>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-0.5">
                                        <span data-testid="imap-health-creds">Credenciales Gmail: <b>{h.credentials_configured ? 'configuradas' : 'NO configuradas'}</b></span>
                                        <span data-testid="imap-health-last-success">Última lectura OK: <b>{fmt(h.last_success_at)}</b>{h.last_success_kind ? ` (${h.last_success_kind})` : ''}</span>
                                        <span data-testid="imap-health-lock">Lock IMAP: <b>{h.lock_holder ? `${h.lock_holder} · ${h.lock_held_seconds}s` : 'libre'}</b>{stuck ? ' ⚠️ posible bloqueo' : ''}</span>
                                        <span data-testid="imap-health-last-error" className="truncate">Último error: <b>{h.last_error ? `${h.last_error} (${fmt(h.last_error_at)})` : 'ninguno'}</b></span>
                                        <span data-testid="imap-health-watchdog" className="sm:col-span-2">Vigilante (reinicio automático a los 15 min sin lectura): <b>{h.restarts || 0} reinicio{h.restarts === 1 ? '' : 's'}</b>{h.last_restart_at ? ` · último ${fmt(h.last_restart_at)} — ${h.last_restart_reason}` : ''}</span>
                                    </div>
                                </div>
                            );
                        })()}
                    </div>
                )}

                {/* Alert for high duplicate rate */}
                {stats.duplicates_pending_review > 3 && (
                    <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center gap-2">
                        <AlertTriangle className="w-5 h-5 text-amber-600" />
                        <span className="text-sm text-amber-700">
                            <strong>Atención:</strong> Hay {stats.duplicates_pending_review} facturas pendientes de revisión por posibles duplicados.
                        </span>
                    </div>
                )}
            </CardContent>
        </Card>
    );
};

export default VerificationStatsPanel;
